import React, { useState, useEffect } from 'react';
import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import dayjs from 'dayjs';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import { useDebounce } from 'use-debounce';
import { useParams, useHistory } from 'react-router-dom';

import Container from 'components/shared/Container';
import Cookies from 'lib/enums/Cookies';
import { DEFAULT_PAGE_SIZE } from 'lib/constants';
import {
  BRANDS_FOR_DROPDOWN,
  GLOBAL_MANUFACTURERS_FOR_DROPDOWN,
  GLOBAL_VENDORS_FOR_DROPDOWN,
  SUPPORT_TICKET,
  USERS_GET_FOR_DROPDOWN,
} from 'lib/networking/endpoints';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import PaginationComponent from 'components/shared/PaginationComponent';
import SearchableScrollableSelect from 'components/shared/SearchableScrollableSelect';
import SupportTicketsTableRow from 'components/settings/support_tickets/SupportTicketsTableRow';
import {
  SupportTicketEntityType,
  SupportTicketEntityTypeLabels,
  SupportTicketStatus,
  SupportTicketStatusOptions,
} from 'lib/enums/SupportTicketEnums';
import useGet from 'lib/hooks/useGet';
import useLocalStorage from 'lib/hooks/useLocalStorage';
import SupportTicketReportExportModal from 'components/settings/support_tickets/SupportTicketReportExportModal';
import SupportTicketsCreateModal from 'components/upb_viewer/SupportTicketsCreateModal';
import SupportTicketsModal from 'components/settings/support_tickets/SupportTicketsModal';

const DATE_FORMAT = 'YYYY-MM-DD';

const entityTypeOptions = Object.entries(SupportTicketEntityTypeLabels).map(([value, label]) => ({
  label,
  value,
}));

const entityNameFetchUrlByType = {
  [SupportTicketEntityType.DISTRIBUTOR]: GLOBAL_VENDORS_FOR_DROPDOWN,
  [SupportTicketEntityType.MANUFACTURER]: GLOBAL_MANUFACTURERS_FOR_DROPDOWN,
  [SupportTicketEntityType.BRAND]: BRANDS_FOR_DROPDOWN,
};

function SupportTicketsView() {
  const [cachedFilters, setCachedFilters] = useLocalStorage(Cookies.SUPPORT_TICKET_FILTERS, {});
  const [selectedStatus, setSelectedStatus] = useState(
    cachedFilters.status || [
      SupportTicketStatus.PENDING_REVIEW,
      SupportTicketStatus.TO_DO,
      SupportTicketStatus.IN_PROGRESS,
    ],
  );
  const [selectedUser, setSelectedUser] = useState(cachedFilters.user || {});
  const [selectedEntityType, setSelectedEntityType] = useState(cachedFilters.entityType || null);
  const [selectedEntityName, setSelectedEntityName] = useState(cachedFilters.entityName || {});
  const [startDate, setStartDate] = useState(
    cachedFilters.startDate ? new Date(cachedFilters.startDate) : null,
  );
  const [endDate, setEndDate] = useState(
    cachedFilters.endDate ? new Date(cachedFilters.endDate) : null,
  );
  const [reporterInput, setReporterInput] = useState(cachedFilters.reporter || '');
  const [reporter] = useDebounce(reporterInput, 300);

  const [showSupportTicketsModal, setShowSupportTicketsModal] = useState(false);
  const [showSupportTicketCreateModal, setShowSupportTicketCreateModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);

  const { ticketId: ticketIdParam } = useParams();
  const ticketId = parseInt(ticketIdParam, 10);
  const history = useHistory();

  const persistFilters = overrides => {
    setCachedFilters({
      status: selectedStatus,
      user: selectedUser,
      entityType: selectedEntityType,
      entityName: selectedEntityName,
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
      reporter: reporterInput,
      ...overrides,
    });
  };

  const entityType = selectedEntityType?.value;
  const entityName = selectedEntityName?.value;

  const {
    data: { tickets, count } = {},
    refetch: refetch,
    loading: loading,
  } = useGet(SUPPORT_TICKET, {
    status: selectedStatus,
    // When a specific Entity Name is picked, the id match alone is unambiguous, so we
    // drop the entity_type filter to avoid excluding legacy tickets whose entity_type
    // was never backfilled. entity_type only drives the type-only case (no name chosen).
    entity_type: entityName ? null : entityType || null,
    vendor_id: entityType === SupportTicketEntityType.DISTRIBUTOR ? entityName || null : null,
    manufacturer_id:
      entityType === SupportTicketEntityType.MANUFACTURER ? entityName || null : null,
    brand_id: entityType === SupportTicketEntityType.BRAND ? entityName || null : null,
    reported_by: reporter || null,
    assigned_to_id: selectedUser?.value || null,
    start_date: startDate ? dayjs(startDate).format(DATE_FORMAT) : null,
    end_date: endDate ? dayjs(endDate).format(DATE_FORMAT) : null,
    limit: itemsPerPage,
    offset: itemsPerPage * (currentPage - 1),
  });
  useEffect(() => {
    if (ticketId) {
      setShowSupportTicketsModal(true);
    }
  }, [ticketId]);
  return (
    <Container>
      <Row className="ml-2">
        <div className="mb-3 d-flex justify-content-between w-100">
          <h3 className="font-weight-bold">Support Tickets</h3>
          <div className="d-flex">
            <Button
              className={css(styles.createTicketButton)}
              variant="outline-primary"
              onClick={() => setShowExportModal(true)}
            >
              Export Report
            </Button>
            <Button
              className={css(styles.createTicketButton)}
              variant="primary"
              onClick={() => setShowSupportTicketCreateModal(true)}
            >
              Create Ticket
            </Button>
          </div>
        </div>
      </Row>
      <Row className="ml-2">
        <Col sm={3}>
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Select
              isMulti
              isClearable
              value={SupportTicketStatusOptions.filter(option =>
                selectedStatus.includes(option.value),
              )}
              options={SupportTicketStatusOptions}
              onChange={options => {
                const nextStatus = options ? options.map(option => option.value) : [];
                setSelectedStatus(nextStatus);
                persistFilters({ status: nextStatus });
                setCurrentPage(1);
              }}
            />
          </Form.Group>
        </Col>
        <Col sm={3}>
          <Form.Group>
            <Form.Label>Entity Type</Form.Label>
            <Select
              isClearable
              value={selectedEntityType}
              options={entityTypeOptions}
              placeholder="Select entity type"
              onChange={option => {
                setSelectedEntityType(option);
                setSelectedEntityName({});
                persistFilters({ entityType: option, entityName: {} });
                setCurrentPage(1);
              }}
            />
          </Form.Group>
        </Col>
        <Col sm={3}>
          <Form.Group>
            <Form.Label>Entity Name</Form.Label>
            {entityType ? (
              <SearchableScrollableSelect
                key={entityType}
                initOption={selectedEntityName}
                onChange={option => {
                  setSelectedEntityName(option || {});
                  persistFilters({ entityName: option || {} });
                  setCurrentPage(1);
                }}
                fetchUrl={entityNameFetchUrlByType[entityType]}
                optionMapper={item => ({ label: item.name, value: item.id })}
                placeholder="Select entity name"
                autoFocus={false}
                allowCreate={false}
              />
            ) : (
              <Select isDisabled placeholder="Select entity type first" />
            )}
          </Form.Group>
        </Col>
        <Col sm={3}>
          <Form.Group>
            <Form.Label>Assigned To</Form.Label>
            <SearchableScrollableSelect
              initOption={selectedUser}
              onChange={option => {
                setSelectedUser(option || {});
                persistFilters({ user: option || {} });
                setCurrentPage(1);
              }}
              fetchUrl={USERS_GET_FOR_DROPDOWN}
              optionMapper={user => ({ label: user.name, value: user.id })}
              placeholder="Select user"
              autoFocus={false}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="ml-2">
        <Col sm={3}>
          <Form.Group>
            <Form.Label>Reporter</Form.Label>
            <Form.Control
              type="text"
              value={reporterInput}
              placeholder="Search by reporter"
              onChange={e => {
                setReporterInput(e.target.value);
                persistFilters({ reporter: e.target.value });
                setCurrentPage(1);
              }}
            />
          </Form.Group>
        </Col>
        <Col sm={3}>
          <Form.Group>
            <Form.Label>Created From</Form.Label>
            <DatePicker
              selected={startDate}
              onChange={date => {
                setStartDate(date);
                persistFilters({ startDate: date ? date.toISOString() : null });
                setCurrentPage(1);
              }}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              customInput={<Form.Control />}
              placeholderText="Start Date"
              dateFormat="yyyy-MM-dd"
              isClearable
            />
          </Form.Group>
        </Col>
        <Col sm={3}>
          <Form.Group>
            <Form.Label>Created To</Form.Label>
            <DatePicker
              selected={endDate}
              onChange={date => {
                setEndDate(date);
                persistFilters({ endDate: date ? date.toISOString() : null });
                setCurrentPage(1);
              }}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              customInput={<Form.Control />}
              placeholderText="End Date"
              dateFormat="yyyy-MM-dd"
              isClearable
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="ml-2 mr-2 align-items-center">
        <Col className="d-flex justify-content-start">
          <span className="text-secondary ml-2">{count} Tickets found</span>
        </Col>
        <Col className="d-flex justify-content-end">
          <PaginationComponent
            totalItems={count || 0}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={size => {
              setItemsPerPage(size);
              setCurrentPage(1);
            }}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            defaultSize
          />
        </Col>
      </Row>
      <Row className="ml-2 mr-2">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>ID</th>
                <th>Task</th>
                <th>Origin</th>
                <th>Entity Type</th>
                <th>Entity Name</th>
                <th>Status</th>
                <th>Assigned To</th>
                <th>Age</th>
              </tr>
            </thead>
            <tbody>
              {tickets?.map(ticket => (
                <SupportTicketsTableRow
                  key={ticket.id}
                  ticket={ticket}
                  refetch={refetch}
                  onClick={() => {
                    history.push(`/support-tickets/${ticket.id}`);
                    setShowSupportTicketsModal(true);
                  }}
                />
              ))}
            </tbody>
          </Table>
        )}
      </Row>
      {showSupportTicketsModal && (
        <SupportTicketsModal
          ticketId={ticketId}
          onHide={ticket => {
            setShowSupportTicketsModal(false);
            history.push('/support-tickets');
          }}
          refetch={refetch}
        />
      )}
      {showSupportTicketCreateModal && (
        <SupportTicketsCreateModal
          onHide={() => setShowSupportTicketCreateModal(false)}
          refetch={refetch}
          product={null}
          typeOfProduct={null}
        />
      )}
      {showExportModal && (
        <SupportTicketReportExportModal onHide={() => setShowExportModal(false)} />
      )}
    </Container>
  );
}

const styles = StyleSheet.create({
  selectContainer: {
    width: '300px',
  },
  createTicketButton: {
    maxHeight: '30px',
    marginLeft: '4px',
    marginRight: '10px',
  },
});

export default SupportTicketsView;
